// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/**
 * Preferences: appearance and hotkeys (PLAN.md P3-T09), cached in this browser. The appearance
 * ones follow a signed-in account to its other devices (preferencesSync.ts); vim, hotkeys, and
 * the formatting bar stay on this device.
 */
import { applyTheme, type ThemePreference } from "@granite/ui";
import { createStore } from "zustand/vanilla";

import { isDesktop } from "./platform.js";

export interface Preferences {
  theme: ThemePreference;
  /** Editor text size in pixels. */
  fontSize: number;
  /** Keep lines to a readable width instead of the full pane. */
  readableWidth: boolean;
  /** The face notes are written in: the text face, or monospaced. */
  editorFont: "sans" | "mono";
  /** Render Markdown while editing (live preview) instead of plain source. */
  livePreview: boolean;
  /** Number the lines on the left of notes, as code editors do. */
  lineNumbers: boolean;
  vim: boolean;
  /** Show the formatting bar under notes. */
  formattingBar: boolean;
  /** Command id → key combination such as "Mod+Shift+F" (overrides the defaults). */
  hotkeys: Record<string, string>;
}

export const defaultPreferences: Preferences = {
  theme: "system",
  fontSize: 15,
  readableWidth: true,
  editorFont: "sans",
  livePreview: true,
  lineNumbers: true,
  vim: false,
  formattingBar: true,
  hotkeys: {},
};

const key = "granite.preferences";

function load(): Preferences {
  try {
    const raw = localStorage.getItem(key);
    const legacyVim = localStorage.getItem("granite.vim") === "1";
    const saved = raw ? (JSON.parse(raw) as Partial<Preferences>) : {};
    return { ...defaultPreferences, vim: legacyVim, ...saved, hotkeys: { ...saved.hotkeys } };
  } catch {
    return defaultPreferences;
  }
}

export const preferences = createStore<Preferences>(load);

/** Applies appearance preferences to the page. */
export function applyPreferences(p: Preferences): void {
  applyTheme(p.theme);
  if (isDesktop) {
    // The window's frame (its border and shadow) follows the app's theme, not only the
    // system's: a light page in a dark-mode window gets a black edge otherwise.
    void import("@tauri-apps/api/window").then((m) => m.getCurrentWindow().setTheme(p.theme === "system" ? null : p.theme));
  }
  const root = document.documentElement;
  root.style.setProperty("--gr-editor-font-size", `${p.fontSize}px`);
  root.dataset.readableWidth = p.readableWidth ? "true" : "false";
  root.dataset.editorFont = p.editorFont;
  root.dataset.lineNumbers = p.lineNumbers ? "true" : "false";
}

preferences.subscribe((p) => {
  try {
    localStorage.setItem(key, JSON.stringify(p));
  } catch {
    // storage blocked: preferences last for this visit
  }
  applyPreferences(p);
});

export function setPreference<K extends keyof Preferences>(k: K, v: Preferences[K]): void {
  preferences.setState({ [k]: v } as Pick<Preferences, K>);
}

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent);

/** "Mod+Shift+F" from a key event ("Mod" is ⌘ or Ctrl). */
export function comboFromEvent(e: KeyboardEvent | { key: string; metaKey: boolean; ctrlKey: boolean; altKey: boolean; shiftKey: boolean }): string | null {
  if (["Control", "Meta", "Alt", "Shift"].includes(e.key)) return null;
  const parts: string[] = [];
  // Either ⌘ or Ctrl counts as "Mod", so shortcuts work with either keyboard.
  if (e.metaKey || e.ctrlKey) parts.push("Mod");
  if (e.altKey) parts.push("Alt");
  if (e.shiftKey) parts.push("Shift");
  const k = e.key.length === 1 ? e.key.toUpperCase() : e.key;
  parts.push(k === " " ? "Space" : k);
  return parts.join("+");
}

/** How a combination is shown: "⌘⇧F" on Apple devices, "Ctrl+Shift+F" elsewhere. */
export function displayCombo(combo: string): string {
  if (!isMac) return combo.replace(/\bMod\b/, "Ctrl");
  const map: Record<string, string> = { Mod: "⌘", Ctrl: "⌃", Alt: "⌥", Shift: "⇧" };
  return combo
    .split("+")
    .map((p) => map[p] ?? p)
    .join("");
}
